import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs/operators';
import { LayoutService } from '../../../core/services/common/layout.service';
import { Menu, NavService } from '../../../core/services/common/nav.service';

/** One block of the side menu: an optional heading and the entries under it. */
interface MenuSection {
  title: string | null;
  /** The group's icon from Menu Master, as the <i> markup MenuService builds; shown on the heading and the icon rail. */
  icon?: string;
  items: Menu[];
  /** The home page's block: its heading is the link to the page itself, with no entries listed under it. */
  isHome?: boolean;
}

/** The icon for a section when Menu Master gives its group none: by the heading's name, then a plain default. */
const SECTION_ICONS: Record<string, string> = {
  dashboard: 'ri-home-4-line',
  organizations: 'ri-building-line',
  localization: 'ri-global-line',
  management: 'ri-stack-line',
  billing: 'ri-bank-card-line',
  settings: 'ri-settings-3-line',
};

const isDashboard = (item: Menu): boolean => !!item.path && item.title?.trim().toLowerCase() === 'dashboard';

function normalizePath(path: string): string {
  const clean = path.split('?')[0].split('#')[0];
  const rooted = clean.startsWith('/') ? clean : `/${clean}`;
  return rooted.length > 1 && rooted.endsWith('/') ? rooted.slice(0, -1) : rooted;
}

/**
 * The side menu, floating on the left. The entries come from Menu Master through NavService; a
 * top-level group becomes a headed section that folds under its heading, and any group inside it
 * opens and closes. Folded on a desktop it stays as a narrow rail with one icon per section.
 */
@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss',
  imports: [RouterLink, NgTemplateOutlet],
})
export class Sidebar {
  private readonly navServices = inject(NavService);
  private readonly router = inject(Router);

  /** The fold / unfold control (the edge handle). */
  readonly layout = inject(LayoutService);

  /** Headed sections the user has open. Every section but Home folds under its heading; the one
   *  holding the current page opens by itself on each navigation, and a click on a heading toggles it. */
  private readonly openSections = signal<ReadonlySet<string>>(new Set());

  readonly sections = computed<MenuSection[]>(() => {
    // The dashboard opens the menu as its own heading, which is the link to the page.
    const items = this.navServices.items();
    const dashboard = items.find(isDashboard) ?? items.flatMap((item) => item.children ?? []).find(isDashboard);
    const sections: MenuSection[] = dashboard
      ? [{ title: dashboard.title ?? 'Dashboard', icon: dashboard.icon, items: [dashboard], isHome: true }]
      : [];
    let loose: MenuSection | null = null;
    for (const item of items) {
      if (item === dashboard) {
        continue;
      }
      if (item.headTitle) {
        sections.push({ title: item.headTitle, items: [] });
        loose = null;
      } else if (item.children?.length) {
        sections.push({ title: item.title ?? null, icon: item.icon, items: item.children.filter((child) => child !== dashboard) });
        loose = null;
      } else if (item.path) {
        // Links that stand on their own at the top level share one block without a heading.
        if (!loose) {
          loose = { title: null, items: [] };
          sections.push(loose);
        }
        loose.items.push(item);
      }
    }
    return sections.filter((s) => s.items.length > 0);
  });

  constructor() {
    // The menu arrives from the API after the first render, so the current page's section and group are opened once it lands.
    effect(() => {
      if (this.navServices.treeVersion() === 0) return;
      untracked(() => this.syncWithUrl());
    });
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.syncWithUrl());
  }

  /** The home heading (a link to the Dashboard) and the untitled block of loose links always show; every other section folds. */
  isCollapsible(section: MenuSection): boolean {
    return !!section.title && !section.isHome;
  }

  isSectionOpen(section: MenuSection): boolean {
    return !this.isCollapsible(section) || this.openSections().has(section.title ?? '');
  }

  /** Whether the entries under a section are showing: an open section stays open underneath, but the rail shows only its heading's icon. */
  showsEntries(section: MenuSection): boolean {
    return this.isSectionOpen(section) && !(this.layout.isRail() && !!section.title);
  }

  toggleSection(section: MenuSection): void {
    const key = section.title ?? '';
    this.openSections.update((open) => {
      const next = new Set(open);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  /** The icon of a section: its Menu Master group's icon, else one by its name, else a plain default. */
  sectionIconClass(section: MenuSection): string {
    const fromMenu = this.iconClasses(section.icon);
    return fromMenu || (SECTION_ICONS[(section.title ?? '').trim().toLowerCase()] ?? 'ri-apps-2-line');
  }

  /** Whether the page you are on is inside this section, so the heading and the rail can mark it. */
  sectionIsCurrent(section: MenuSection): boolean {
    const hit = (item: Menu): boolean => !!item.selected || !!item.children?.some(hit);
    return section.items.some(hit);
  }

  /** A section's icon in the rail: unfolds the sidebar with that section open. (Home's heading is a link, so it goes to its page.) */
  onRailSection(section: MenuSection): void {
    const key = section.title ?? '';
    this.openSections.update((open) => new Set(open).add(key));
    this.layout.onMenuToggle();
  }

  /** The menu's icons arrive as markup (<i class="bx bx-home side-menu__icon">); only its classes are used. */
  menuIconClass(icon: string | undefined): string {
    return this.iconClasses(icon) || 'pi pi-fw pi-circle-fill ui-menu-dot';
  }

  /** Opens or folds a group; opening one folds the groups beside it, so only one is open at a time. */
  toggleGroup(event: Event, target: Menu): void {
    event.preventDefault();
    // In the icon rail a group has nowhere to open: unfold the sidebar first, then the group opens in it.
    if (this.layout.isRail()) this.layout.onMenuToggle();
    target.active = !target.active;
    if (target.active) {
      this.foldSiblings(this.navServices.items(), target);
    }
    this.navServices.refresh();
  }

  private iconClasses(icon: string | undefined): string {
    const match = /class\s*=\s*["']([^"']+)["']/.exec(icon ?? '');
    return (match?.[1] ?? '').split(/\s+/).filter((c) => c && c !== 'side-menu__icon').join(' ');
  }

  private foldSiblings(level: Menu[], target: Menu): boolean {
    if (level.includes(target)) {
      level.filter((item) => item !== target).forEach((item) => this.fold(item));
      return true;
    }
    return level.some((item) => this.foldSiblings(item.children ?? [], target));
  }

  private fold(item: Menu): void {
    item.active = false;
    item.children?.forEach((child) => this.fold(child));
  }

  /** Marks the current page and opens the groups and the section above it; unrelated groups fold, so only that branch stays open. */
  private syncWithUrl(): void {
    const url = normalizePath(this.router.url);
    const items = this.navServices.items();
    const current = this.currentPage(items, url);
    items.forEach((item) => this.markBranch(item, current));
    this.navServices.refresh();
    if (current) {
      const section = this.sections().find((s) => this.isCollapsible(s) && this.sectionIsCurrent(s));
      if (section?.title && !this.openSections().has(section.title)) {
        const key = section.title;
        this.openSections.update((open) => new Set(open).add(key));
      }
    }
  }

  private markBranch(item: Menu, current: Menu | null): boolean {
    const childHit = (item.children ?? []).map((child) => this.markBranch(child, current)).some(Boolean);
    const hit = item === current || childHit;
    item.active = hit;
    item.selected = hit;
    return hit;
  }

  /** The entry for this URL: the one with the longest path that is the URL or sits above it, because a record opened from a
   *  list (…/plans/5) belongs to that list. Longest, because one entry's path can sit under another's (…/plans and
   *  …/plans/module-matrix), and only one may light up. The dashboard only matches itself: pages below its path are not it. */
  private currentPage(items: Menu[], url: string): Menu | null {
    let best: Menu | null = null;
    let bestLength = -1;
    const visit = (item: Menu): void => {
      if (item.path) {
        const path = normalizePath(item.path);
        const matches = url === path || (!isDashboard(item) && url.startsWith(`${path}/`));
        if (matches && path.length > bestLength) {
          best = item;
          bestLength = path.length;
        }
      }
      item.children?.forEach(visit);
    };
    items.forEach(visit);
    return best;
  }
}
